import React, { useState } from 'react';
import { IPriceSales } from '@/interfaces/price-sales';
import Title from '@/components/title';
import { searchPriceSales, uploadQuotation } from '@/adapters/price-sales';
import {
  ISearchPriceSalesReq,
  ISearchPriceSalesRes,
} from '@/interfaces/price-sales';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ICustomer } from '@/interfaces/customer';
import { Button, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { PlusOutlined } from '@ant-design/icons';
import { IDataModal } from '@/interfaces/dataModal';
import PriceSalesTable from './components/price-sales-table';
import PriceSalesModal from './components/price-sales-modal';
import Search from 'antd/es/input/Search';
import MediaUploader from '@/components/media/uploader-modal';

interface IProps {
  data: ICustomer;
}

const _criteria: ISearchPriceSalesReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
  customerId: 0,
};

const _modal: IDataModal<IPriceSales> = {
  open: false,
};

const PricesSales = ({ data }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchPriceSalesReq>({
    ..._criteria,
  });
  const [dataModal, setDataModal] = useState<IDataModal<IPriceSales>>(_modal);
  const [updateModal, setUploadModal] =
    useState<IDataModal<IPriceSales>>(_modal);

  const {
    data: priceSales,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<AxiosResponse<ISearchPriceSalesRes>, Error>({
    queryKey: ['search-price-sales', criteria],
    queryFn: async () =>
      await searchPriceSales({ ...criteria, customerId: Number(data?.id) }),
    enabled: !!data?.id,
  });

  const onRefetch = () => {
    refetch();
    setDataModal({
      ..._modal,
    });
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IPriceSales> | SorterResult<IPriceSales>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IPriceSales) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
    });
  };

  return (
    <>
      <Title customClassName="text-lg text-blue-700" title={`Price Sales`}>
        <Button
          ghost
          type="primary"
          className="max-w-[90px]"
          onClick={() => {
            setDataModal({
              ...dataModal,
              open: true,
            });
          }}
        >
          <PlusOutlined /> เพิ่มใหม่
        </Button>
      </Title>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row gap-3">
          <Search
            className="md:max-w-[400px] md:min-w-[400px]"
            placeholder="ค้นหา"
            allowClear
            size="large"
            enterButton="ค้นหา"
            onSearch={onSearch}
          />
        </div>
        <PriceSalesTable
          criteria={criteria}
          priceSales={priceSales?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isRefetching}
          onEditData={item => setDataModal({ id: item.id, item, open: true })}
          onDelete={onRefetch}
          onTableChange={onTableChange}
          onUploadQuotation={id => {
            setUploadModal({
              open: true,
              id: id,
            });
          }}
        />
      </div>

      <MediaUploader
        data={{
          modelId: Number(updateModal?.id),
          modelType: 'quotation',
        }}
        open={updateModal.open}
        onCancel={() => {
          setUploadModal({
            ..._modal,
          });
        }}
        onUploaded={() => {
          setUploadModal({
            ..._modal,
          });
          refetch();
        }}
        ExternalApi={data => uploadQuotation(data)}
      />

      <PriceSalesModal
        customerId={data?.id}
        dataModal={dataModal}
        handleCancel={() =>
          setDataModal({
            ..._modal,
          })
        }
        handleOk={() => onRefetch()}
      />
    </>
  );
};

export default PricesSales;
